import { useState } from 'react'
import { X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { SupplierImportCandidateDto, SupplierOverviewDto } from '@/lib/api/v2/planning'
import { formatDate } from '@/lib/format'
import { withBrand, type ImportLineState } from '@/lib/supplierImport'

const NEW_SUPPLIER = '__new__'

interface SupplierImportRowProps {
  candidate: SupplierImportCandidateDto
  line: ImportLineState
  suppliers: SupplierOverviewDto[]
  error?: { message: string; suggestedTargetSupplierId: string | null }
  disabled: boolean
  onChange: (line: ImportLineState) => void
}

/**
 * One proposal (03 § S5): everything needed to decide without opening anything — the name (editable),
 * what will happen (« Nouveau fournisseur » or « Ajouter à : X »), the domain and the addresses seen,
 * the brands (removable, addable), two subjects quoted as written, how many emails and the last one.
 * Never a confidence percentage.
 */
export function SupplierImportRow({ candidate: c, line, suppliers, error, disabled, onChange }: SupplierImportRowProps) {
  const [brandDraft, setBrandDraft] = useState('')
  const isPlatform = c.kind === 'Platform'
  const set = (patch: Partial<ImportLineState>) => onChange({ ...line, ...patch })
  const addBrand = () => {
    set({ brands: withBrand(line.brands, brandDraft) })
    setBrandDraft('')
  }
  const suggested = error?.suggestedTargetSupplierId ? suppliers.find((s) => s.id === error.suggestedTargetSupplierId) : undefined

  return (
    <li className="flex gap-3 border-b px-3 py-3 last:border-b-0" data-testid="supplier-import-row" data-domain={c.domain}>
      <Checkbox
        checked={line.checked}
        onCheckedChange={(v) => set({ checked: v === true })}
        disabled={disabled}
        aria-label={`Ajouter ${line.name}`}
        className="mt-2"
        data-testid="supplier-import-row-checkbox"
      />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {isPlatform ? (
            <p className="py-1 text-sm font-medium" data-testid="supplier-import-row-platform">
              {line.name} — plateforme de commande
            </p>
          ) : (
            <>
              {line.targetSupplierId === null && (
                <Input
                  value={line.name}
                  onChange={(e) => set({ name: e.target.value })}
                  disabled={disabled}
                  className="h-8 w-56"
                  aria-label="Nom du fournisseur"
                  data-testid="supplier-import-row-name-input"
                />
              )}
              <Select
                value={line.targetSupplierId ?? NEW_SUPPLIER}
                onValueChange={(v) => set({ targetSupplierId: v === NEW_SUPPLIER ? null : v })}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 w-auto min-w-44" aria-label="Que faire de cette ligne" data-testid="supplier-import-row-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NEW_SUPPLIER}>Nouveau fournisseur</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      Ajouter à : {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        <p className="break-all text-xs text-muted-foreground" data-testid="supplier-import-row-addresses">
          {c.domain}
          {c.addresses.length > 0 && ` · ${c.addresses.map((a) => a.address).join(', ')}`}
        </p>

        {!isPlatform && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="supplier-import-row-brands">
            {line.brands.map((b) => (
              <Badge key={b} variant="secondary" className="gap-1">
                {b}
                <button
                  type="button"
                  onClick={() => set({ brands: line.brands.filter((x) => x !== b) })}
                  disabled={disabled}
                  aria-label={`Retirer la marque ${b}`}
                  className="rounded-sm hover:text-foreground"
                  data-testid="supplier-import-row-brand-remove-btn"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Input
              value={brandDraft}
              onChange={(e) => setBrandDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addBrand()
                }
              }}
              onBlur={() => brandDraft.trim() && addBrand()}
              disabled={disabled}
              placeholder="+ marque"
              className="h-7 w-28 text-xs"
              aria-label="Ajouter une marque"
              data-testid="supplier-import-row-brand-input"
            />
          </div>
        )}

        <ul className="space-y-0.5 text-sm" data-testid="supplier-import-row-evidence">
          {c.evidence.map((e, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="w-12 shrink-0 text-xs text-muted-foreground">{e.sent ? 'envoyé' : 'reçu'}</span>
              <span className="min-w-0 break-words">« {e.subject} »</span>
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground">
          {c.sentCount + c.receivedCount} email{c.sentCount + c.receivedCount > 1 ? 's' : ''} · dernier le {formatDate(c.lastSeenAt)}
        </p>

        {error && (
          <div className="flex flex-wrap items-center gap-2" data-testid="supplier-import-row-error">
            <p className="text-sm text-destructive">{error.message}</p>
            {suggested && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => set({ targetSupplierId: suggested.id, checked: true })}
                data-testid="supplier-import-row-attach-btn"
              >
                Ajouter à : {suggested.name}
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  )
}
